import { useCallback, useEffect, useRef, useState } from 'react';

import {
  MIN_HEIGHT,
  MIN_WIDTH,
  type PanelFrame,
  useDataInspectorStore,
} from './data-inspector.store';

/** Gap kept between the panel and the viewport edges when placed by default. */
const EDGE_MARGIN = 16;

/**
 * Clear of the app's top bar. The whole reason the panel floats rather than
 * filling the right edge is that the page navigation lives up there, and a
 * full-height drawer sat on top of it.
 */
const DEFAULT_TOP = 96;

/** Keep at least this much of the panel on screen, so it can always be grabbed. */
const MIN_VISIBLE = 80;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

type Viewport = { width: number; height: number };

const readViewport = (): Viewport => ({
  width: window.innerWidth,
  height: window.innerHeight,
});

/**
 * Resolves the stored frame against the current viewport.
 *
 * Position is clamped here rather than on write because the constraint depends
 * on the viewport AND the panel's own size, both of which change after a drag:
 * a panel parked bottom-right on a large monitor would otherwise be stranded
 * off-screen when the same session is reopened on a laptop.
 */
export const usePanelFrame = () => {
  const frame = useDataInspectorStore((state) => state.frame);
  const setFrame = useDataInspectorStore((state) => state.setFrame);
  const [viewport, setViewport] = useState<Viewport>(readViewport);

  useEffect(() => {
    const onResize = () => setViewport(readViewport());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const width = Math.min(frame.width, viewport.width - EDGE_MARGIN * 2);
  const height = Math.min(frame.height, viewport.height - EDGE_MARGIN * 2);

  // Unplaced panels sit top-right, below the nav — where a right-docked drawer
  // used to be, minus the part that covered the page chrome.
  const defaultX = Math.max(EDGE_MARGIN, viewport.width - width - EDGE_MARGIN);
  const x = clamp(
    frame.x ?? defaultX,
    MIN_VISIBLE - width,
    viewport.width - MIN_VISIBLE
  );
  const y = clamp(frame.y ?? DEFAULT_TOP, 0, viewport.height - MIN_VISIBLE);

  // Every drag handler reads position through this ref rather than the render
  // closure. A pointermove applies a delta relative to the LAST event, so a
  // handler that captured a stale x/y would drop that delta permanently and the
  // panel would lag behind the cursor, a bit further with each batched frame.
  const positionRef = useRef({ x, y, width, height });
  positionRef.current = { x, y, width, height };

  /** Moves by a pointer delta, pinning the result inside the viewport. */
  const moveBy = useCallback(
    (deltaX: number, deltaY: number) =>
      setFrame({
        x: positionRef.current.x + deltaX,
        y: positionRef.current.y + deltaY,
      }),
    [setFrame]
  );

  /**
   * Resizing from the left edge keeps the RIGHT edge still: the panel lives on
   * the right of the screen, and growing it toward the page reads as widening
   * the tool rather than sliding it sideways.
   */
  const resizeFromLeft = useCallback(
    (clientX: number) => {
      const right = positionRef.current.x + positionRef.current.width;
      const nextWidth = clamp(right - clientX, MIN_WIDTH, right);
      setFrame({ width: nextWidth, x: right - nextWidth });
    },
    [setFrame]
  );

  const resizeFromBottom = useCallback(
    (clientY: number) =>
      setFrame({
        height: Math.max(clientY - positionRef.current.y, MIN_HEIGHT),
      }),
    [setFrame]
  );

  const resizeFromRight = useCallback(
    (clientX: number) =>
      setFrame({
        width: Math.max(clientX - positionRef.current.x, MIN_WIDTH),
      }),
    [setFrame]
  );

  const resolved: Required<Omit<PanelFrame, 'x' | 'y'>> & {
    x: number;
    y: number;
  } = { x, y, width, height };

  return {
    frame: resolved,
    moveBy,
    resizeFromLeft,
    resizeFromRight,
    resizeFromBottom,
  };
};
